import { useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { motion } from 'motion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { KeyNames, rgba, useModalControl, useTabbable } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR, EVENT_BACKGROUND_COLOR } from '../../../constants';
import { useGetAppointmentDetails } from '../../../hooks';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';
import { getDateString } from '../../../utils/date-helpers';
import { OutOfOfficeEventCardModal } from '../../modals/out-of-office-event-card-modal';
import { PersonStatusIndicator } from '../../schedule-calendar-components/appointment-event-card/person-status-indicator';
import { EVENT_CARD_COLOR_LINE_WIDTH } from '../constants';
import { useCalendarView, useCalendarViewProps } from '../providers';
import { EventType } from '../types';
import {
  checkIsNonAttendedEventStatus,
  getDurationFromHours,
  getEventCardCssForVerticalView,
  maxTotalGridColumns,
  zIndex,
} from '../utils';
import { useZoomSafeContext } from '../zoom';
import { DraggableEventCardWrapper } from './draggable-event-card-wrapper';
import { createColumnId, getEventPlacementValues, getNonAttendedEventStatusIconName } from './helper';
import { getOverlappingEventIndicatorVariant } from './overlapping-event-indicator';

type EventCardProps = {
  id: string;
  locationName: string;
  locationId?: string;
  entityId?: string;
  entityName?: string;
  title: string;
  startDate: string;
  calendarDate?: string;
  startHour: string;
  endHour: string;
  appointmentType?: string;
  eventTypeColor?: string;
  laneCount?: number;
  laneIndex?: number;
  type: EventType;
  visibleLaneCount: number;
  personStatus?: string;
  eventStatus?: EventStatus;
  backgroundColor?: string; // HEX color
  isDragging?: boolean;
  showAnimated?: boolean;
  isDraggingCurrentCard?: boolean;
  hasPendingDrop?: boolean;
};

export const CalendarEventCardV3 = ({
  id,
  title,
  type,
  locationName,
  locationId,
  entityId,
  entityName,
  startDate,
  calendarDate,
  startHour,
  endHour,
  eventTypeColor,
  laneCount = 0,
  laneIndex = 0,
  visibleLaneCount,
  personStatus,
  eventStatus,
  appointmentType,
  backgroundColor = EVENT_BACKGROUND_COLOR,
  isDragging = false,
  showAnimated = true,
  isDraggingCurrentCard = false,
  hasPendingDrop = false,
}: EventCardProps) => {
  const [isTapped, setIsTapped] = useState(false);
  const { pixelsPerMinute } = useZoomSafeContext();

  const { startHour: calendarStartHour, endHour: calendarEndHour, EventCardComponent } = useCalendarViewProps();
  const { floatingTooltipData, setBackgroundView, setFloatingTooltipData } = useCalendarView([
    'floatingTooltipData',
    'setFloatingTooltipData',
    'setBackgroundView',
  ]);

  const eventCardContentWrapperRef = useRef<HTMLDivElement | null>(null);

  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);
  const isAppointment = type === 'appointment';
  const primaryColor = eventTypeColor ?? (isAppointment ? PRIMARY_EVENT_COLOR : theme.colors.neutral50);

  const isNonAttendedEvent = checkIsNonAttendedEventStatus(eventStatus);

  const { triggerProps: outOfOfficeTriggerProps, modalProps: outOfOfficeModalProps } = useModalControl({
    onClose: () => collapseEventCard(),
  });
  const {
    openModal: openAppointmentModal,
    Modal: AppointmentDetailsModal,
    isSelected: isEventCardExpanded,
  } = useGetAppointmentDetails({
    appointmentId: id,
    locationId: locationId ?? '',
    eventColor: primaryColor,
    onModalClose: () => collapseEventCard(),
    enabled: isAppointment,
  });

  const expandEventCard = () => {
    if (isAppointment) {
      openAppointmentModal();
    } else {
      outOfOfficeTriggerProps.onClick();
      setBackgroundView(true);
    }
  };

  const collapseEventCard = () => {
    setBackgroundView(false);
    setFloatingTooltipData(undefined);
    setIsTapped(false);
  };

  useTabbable({ ref: eventCardContentWrapperRef, autofocus: 'first' });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // need to ensure the event is fired from the event card wrapper div
    if (e.target !== e.currentTarget) {
      return;
    }
    if ((e.key === KeyNames.Enter || e.key === KeyNames.Space) && !isEventCardExpanded) {
      e.preventDefault();
      expandEventCard();
      eventCardContentWrapperRef.current?.focus();
    }
    if (e.key === KeyNames.Escape && isEventCardExpanded) {
      e.preventDefault();
      collapseEventCard();
    }
  };

  const placementValues = getEventPlacementValues({
    calendarDate: calendarDate ?? '',
    // for exceptions we were not getting startDate so need to use calendar date
    // TODO: we need to update this once we handle exceptions correctly for overlapping cases
    startDate: startDate ?? getDateString(calendarDate) ?? '',
    startHour,
    endHour,
    pixelsPerMinute,
  });

  const shouldHideEventCard = useMemo(() => {
    const maxTotalMinutes = maxTotalGridColumns(calendarStartHour, calendarEndHour);

    return !(
      (placementValues.startGridColumn <= 1 && placementValues.endGridColumn <= 1) ||
      (placementValues.startGridColumn === maxTotalMinutes && placementValues.endGridColumn === maxTotalMinutes) ||
      // hide event card if it's outside of the visible lane count
      laneIndex >= visibleLaneCount
    );
  }, [
    laneIndex,
    visibleLaneCount,
    placementValues.endGridColumn,
    placementValues.startGridColumn,
    calendarStartHour,
    calendarEndHour,
  ]);
  const overlappingEventIndicatorVariant = useMemo(
    () =>
      placementValues.isOverlappingEvent
        ? getOverlappingEventIndicatorVariant(placementValues.totalDuration)
        : undefined,
    [placementValues.totalDuration, placementValues.isOverlappingEvent]
  );

  // Calculate event position for drag placeholder
  const eventPosition = useMemo(() => {
    const eventCardCss = getEventCardCssForVerticalView(visibleLaneCount, laneCount, laneIndex) as {
      width: string;
      left: string;
    };
    return {
      top: placementValues.startGridColumn,
      height: (placementValues.totalDuration - 1) * pixelsPerMinute,
      width: eventCardCss?.width ?? '100%',
      left: eventCardCss?.left ?? '0',
    };
  }, [
    visibleLaneCount,
    laneCount,
    laneIndex,
    placementValues.startGridColumn,
    placementValues.totalDuration,
    pixelsPerMinute,
  ]);

  const WrapperComponent = showAnimated ? motion.div : 'div';
  const wrapperProps = showAnimated ? { layout: true, initial: false } : {};

  // Reset floating tooltip while showing drag and drop confirmation modal
  useEffect(() => {
    if (hasPendingDrop && isDraggingCurrentCard && floatingTooltipData) {
      setFloatingTooltipData(undefined);
    }
  }, [floatingTooltipData, hasPendingDrop, isDraggingCurrentCard, setFloatingTooltipData]);

  return (
    <WrapperComponent
      {...wrapperProps}
      data-event-id={id} // use to focus event card in onboarding-flow
      data-event='true'
      tabIndex={isEventCardExpanded ? -1 : 0}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setFloatingTooltipData({ name: title, value: duration, appointmentType: appointmentType })}
      onMouseLeave={() => setFloatingTooltipData(undefined)}
      onTouchEnd={(event) => {
        // If the event card with non attended status is tapped first time, show the text on card preview
        // and open the card modal on second tap
        if (!isTapped && checkIsNonAttendedEventStatus(eventStatus)) {
          event.preventDefault();
          setIsTapped(true);
        } else if (!isEventCardExpanded) {
          event.preventDefault();
          expandEventCard();
        }
      }}
      css={[
        !shouldHideEventCard &&
          css`
            display: none;
          `,
        (isDragging || (hasPendingDrop && isDraggingCurrentCard)) &&
          css`
            opacity: 0;
            cursor: grabbing;
            pointer-events: none;
          `,
        isDragging &&
          !isDraggingCurrentCard &&
          css`
            opacity: 0.5;
          `,
        css`
          position: absolute;
          top: ${placementValues.startGridColumn}px;
          height: ${(placementValues.totalDuration - 1) * pixelsPerMinute}px;
          transition: width 0.5s ease-in-out, height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        `,
        getEventCardCssForVerticalView(visibleLaneCount, laneCount, laneIndex),
        css`
          user-select: none;
          outline: none;
          &::after {
            content: '';
            width: calc(100% + 8px);
            height: 100%;
            position: absolute;
            left: 50%;
            top: 50%;
            pointer-events: none;
            border-radius: ${theme.borderRadius.medium};
            opacity: 0;
            transition: 0.2s ease;
            transform: translate(-50%, -50%);
            border: 2px solid ${theme.colors.primary30};
          }
          &:focus-visible {
            &::after {
              opacity: 1;
            }
          }
        `,
        isNonAttendedEvent && nonAttendedEventStyle(isEventCardExpanded, isTapped),
        !isEventCardExpanded &&
          css`
            cursor: pointer;
          `,
        // Show drag handle on hover when appointment reordering is enabled
        css`
          &:hover {
            .event-card-drag-handle {
              display: block;
            }
          }
        `,
      ]}
      style={{ zIndex: zIndex.eventCardDefault + (isEventCardExpanded ? 1 : 0) }}
    >
      <DraggableEventCardWrapper
        eventId={id}
        eventPosition={eventPosition}
        isAppointment={isAppointment}
        sourceColumnId={createColumnId(locationId, entityId)}
        isDraggingCurrentCard={isDraggingCurrentCard}
      >
        <NonAttendedEventStatusIcon eventStatus={eventStatus} />
        <motion.article
          data-event-id={id}
          tabIndex={isEventCardExpanded ? 0 : -1}
          onKeyDown={handleKeyDown}
          data-trackingid={
            isAppointment ? CALENDAR_TRACKING_IDS.appointmentEventCard : CALENDAR_TRACKING_IDS.outOfOfficeEventCard
          }
          ref={eventCardContentWrapperRef}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => {
            if (!isEventCardExpanded) expandEventCard();
          }}
          css={[
            css`
              border-radius: ${theme.borderRadius.small};
              padding: ${theme.spacing(0.5, 0.5, 0, 1.5)};
              box-shadow: ${theme.shadows.light};
              position: relative;
              overflow: hidden;
              outline: none;
              background: ${isNonAttendedEvent
                ? 'transparent'
                : isAppointment
                ? backgroundColor
                : theme.colors.neutral20};
              height: 100%;
              transition: box-shadow 0.2s ease-in-out, outline 0.1s linear;
              &:hover {
                box-shadow: ${theme.shadows.floating};
              }
            `,
            isNonAttendedEvent &&
              css`
                opacity: ${isEventCardExpanded ? 0.5 : 0};
                transition: opacity 0.3s ease-in-out;
              `,
            isEventCardExpanded &&
              css`
                outline: 2px solid ${theme.colors.primary50};
                outline-offset: 2px;
              `,
          ]}
        >
          <PersonStatusIndicator personStatus={personStatus} height={4} borderRadius='small' isExpanded={false} />

          <EventColoredLine eventColor={primaryColor} />
          {/* remove this condition to allow modal to animate */}
          {AppointmentDetailsModal}
          <OutOfOfficeEventCardModal
            id={id}
            eventType={type}
            title={title}
            duration={duration}
            locationId={locationId}
            locationName={locationName}
            providerId={entityId}
            startDate={startDate}
            providerName={entityName}
            calendarDate={calendarDate}
            modalProps={outOfOfficeModalProps}
          />
          <EventCardComponent
            id={id}
            eventType={type}
            title={title}
            duration={duration}
            locationName={locationName}
            locationId={locationId}
            providerId={entityId}
            providerName={entityName}
            isExpanded={isEventCardExpanded}
            startDate={startDate}
            isYesterdayEvent={placementValues.isYesterdayEvent}
            overlappingIndicatorVariant={overlappingEventIndicatorVariant}
          />
        </motion.article>
      </DraggableEventCardWrapper>
    </WrapperComponent>
  );
};

const NonAttendedEventStatusIcon = ({ eventStatus }: { eventStatus?: EventStatus }) => {
  const eventStatusIconName = getNonAttendedEventStatusIconName(eventStatus);
  if (!eventStatusIconName) return null;

  return (
    <Icon
      name={eventStatusIconName}
      color='error'
      size={16}
      css={{
        position: 'absolute',
        top: theme.spacing(0.5),
        right: theme.spacing(0.5),
        transition: 'opacity 0.2s ease-in-out',
      }}
    />
  );
};

const EventColoredLine = ({ eventColor }: { eventColor: string }) => {
  return (
    <div
      style={{
        position: 'absolute',
        background: eventColor,
        left: 0,
        top: 0,
        width: EVENT_CARD_COLOR_LINE_WIDTH,
        height: '100%',
      }}
    />
  );
};

const nonAttendedEventHoverStyle = css({
  background: theme.colors.white,
  borderColor: 'transparent',
  '& > div > article': {
    opacity: 0.5,
  },
  '& > div > svg': {
    opacity: 0,
  },
});

const nonAttendedEventStyle = (isExpanded: boolean, isTapped: boolean) =>
  css({
    background: rgba(theme.colors.critical20, 0.2),
    border: `1px dashed ${theme.colors.critical50}`,
    borderRadius: theme.borderRadius.small,
    transition: 'all 0.3s cubic-bezier(0.7, -0.4, 0.4, 1.4)',

    '&:hover, &:focus-visible': {
      ...nonAttendedEventHoverStyle,
    },

    ...(isTapped && {
      ...nonAttendedEventHoverStyle,
    }),

    ...(isExpanded
      ? {
          background: theme.colors.white,
          borderColor: 'transparent',
        }
      : {}),
  });
